import * as vp from 'vopay';
import React, { FunctionComponent, useContext } from 'react';

import BillingPackageCreationContext from './BillingPackageCreationContext';

import type { NewBillingPackageData } from '../types';
import type { FieldData, SubSection } from '../../../hooks/useFields/types';

const { formatAsCurrency, Box, Icon, Table, SubTitle } = vp;

type Row = {
  [key: string]: string | React.JSX.Element;
};

const ReviewSection: FunctionComponent<Record<never, never>> = () => {
  const { fields, values } = useContext(BillingPackageCreationContext);
  const { getSection, getSectionIds } = fields;
  const ctx = useContext(BillingPackageCreationContext);

  const reviewData = getSectionIds().map((sectionId) => {
    const section = getSection(sectionId);

    return section.map((subsection) => {
      const tableProps: vp.TableProps = {
        name: `${sectionId}-${subsection.label}`,
        variant: 'pair',
        columns: {
          key: { header: 'Key' },
          input: { header: 'Input' },
        },
        rows: buildRows(values, subsection),
      };

      return (
        hasRows(tableProps) && (
          <Box key={`${sectionId}-${subsection.label}`} mb="lg" flx="col">
            <Box pt="lg" flx="col">
              <SubTitle subTitle={subsection.label} variant="small" />
              <Table {...tableProps} />
            </Box>
          </Box>
        )
      );
    });
  });

  return <Box flx="col">{reviewData}</Box>;

  function buildRows(newAccountData: NewBillingPackageData, subsection: SubSection<NewBillingPackageData>): Row[] {
    return Object.values(subsection.fields)
      .filter((fieldData) => fieldData && fieldData.showField(ctx))
      .map((fieldData) =>
        fieldData
          ? buildRow(fieldData, newAccountData[fieldData.fieldName])
          : {
              key: '',
              input: '',
            },
      )
      .filter(({ input }) => input);
  }

  function buildRow(fieldData: FieldData<NewBillingPackageData>, input: string | boolean | number | undefined): Row {
    return {
      key: fieldData.label,
      input: input ? getDataTypeHandler(fieldData)(input) : '',
    };
  }

  function getDataTypeHandler(
    fieldData: FieldData<NewBillingPackageData>,
  ): (userInput: string | boolean | number | vp.TierOption<string>[]) => string | React.JSX.Element {
    const handlers = {
      number(userInput: string | boolean | number | vp.TierOption<string>[]) {
        return hasStringInput(userInput) ? userInput : '';
      },

      boolean(userInput: string | boolean | number | vp.TierOption<string>[]) {
        return hasBooleanInput(userInput) ? <Icon icon="check" size="sm" /> : '';
      },

      text(userInput: string | boolean | number | vp.TierOption<string>[]) {
        return hasStringInput(userInput) ? userInput : '';
      },

      textarea(userInput: string | boolean | number | vp.TierOption<string>[]) {
        return hasStringInput(userInput) ? (
          fieldData.formatValue ? (
            <div style={{ whiteSpace: 'pre-line' }}>{userInput}</div>
          ) : (
            userInput
          )
        ) : (
          ''
        );
      },

      currency(userInput: string | boolean | number | vp.TierOption<string>[]) {
        return hasStringInput(userInput) ? (
          <Box ai="center">
            <Icon icon="dollar" size="sm" />
            {formatAsCurrency(parseFloat(userInput.replace(/,/g, '')).toFixed(2), '')}
          </Box>
        ) : (
          ''
        );
      },

      percentage(userInput: string | boolean | number | vp.TierOption<string>[]) {
        return hasStringInput(userInput) ? (
          <Box ai="center">
            <Icon icon="percentage" size="sm" />
            {userInput}
          </Box>
        ) : (
          ''
        );
      },

      email(userInput: string | boolean | number | vp.TierOption<string>[]) {
        return hasStringInput(userInput) ? userInput : '';
      },

      mapped(userInput: string | boolean | number | vp.TierOption<string>[]) {
        const map = (fieldData.getSelectOptions && fieldData.getSelectOptions(ctx)) || {};
        return hasStringInput(userInput) ? map[userInput] : '';
      },

      link(userInput: string | boolean | number | vp.TierOption<string>[]) {
        let linkName = 'Link';
        if (fieldData && fieldData.linkName) linkName = fieldData.linkName(ctx);
        const link: string = userInput.toString().trim() || '';
        return (
          <p>
            <a href={link}>{linkName}</a>
          </p>
        );
      },

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      file(_userInput: string | boolean | number | vp.TierOption<string>[]) {
        let fileName = 'Uploaded Successfully';
        if (fieldData && fieldData.fileName) fileName = fieldData.fileName(ctx);
        return hasStringInput(fileName) ? fileName : '';
      },

      error() {
        return '';
      },
    };

    return handlers[fieldData.dataType];
  }

  function hasStringInput(_userInput: string | boolean | number | vp.TierOption<string>[]): _userInput is string {
    return typeof _userInput === 'string' && _userInput !== '';
  }

  function hasBooleanInput(_userInput: string | boolean | number | vp.TierOption<string>[]): _userInput is boolean {
    return typeof _userInput === 'boolean' && _userInput === true;
  }

  function hasRows({ rows }: vp.TableProps) {
    return rows.length > 0;
  }
};

export default ReviewSection;
